import React from 'react';
import { Box, Typography } from '@mui/material';
import Tags from '../common/Tags';
import './WidgetResultsRenderer.css';

// Helper: render markdown bold (**text**) as actual bold
const renderMarkdownBold = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

// Helper: parse string items formatted like "Title: detail".
// Content may span multiple lines (custom prompts can request several "label: value" lines per section);
// only the first line's leading label is treated as the title.
const parseStringItem = (text) => {
  if (typeof text !== 'string') return null;
  const match = text.match(/^\s*([^:\n]+):\s*([\s\S]+?)\s*$/);
  if (!match) return null;
  const title = match[1].trim();
  const content = match[2].trim();
  if (!title || !content) return null;
  return { title, content };
};

const getItemTextField = (item) => {
  if (item == null || typeof item !== 'object') return null;
  const text = item.content ?? item.issue ?? item.recommendation ?? item.point ?? item.title ?? '';
  return typeof text === 'string' ? text : null;
};

// Helper: get display text from item (object with title/content or issue/recommendation/point, or string)
const getItemContent = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') {
    const parsed = parseStringItem(item);
    return parsed ? parsed.content : item;
  }
  const text = getItemTextField(item) ?? '';
  const parsed = parseStringItem(text);
  return parsed ? parsed.content : text;
};

// Helper: get optional title from item (object or string formatted like "Title: detail")
const getItemTitle = (item) => {
  if (item == null) return null;
  if (typeof item === 'string') {
    const parsed = parseStringItem(item);
    return parsed ? parsed.title : null;
  }
  if (typeof item === 'object' && typeof item.title === 'string') return item.title;
  const text = getItemTextField(item);
  const parsed = parseStringItem(text);
  return parsed ? parsed.title : null;
};

// Shared list render: <ul>/<li> with listStyle: 'disc', listStylePosition: 'outside'; supports title + content and object/string
const renderResultList = (items, keyPrefix = 'item', titles = []) => {
  if (!items || !Array.isArray(items) || items.length === 0) return null;
  return (
    <ul
      style={{
        listStyle: 'disc',
        listStylePosition: 'outside',
        margin: '0 0 8px 0',
        paddingLeft: '1.2em',
      }}
    >
      {items.map((item, index) => {
        const content = getItemContent(item);
        const fallbackTitle = Array.isArray(titles) ? titles[index] : null;
        const title = getItemTitle(item) ?? (typeof fallbackTitle === 'string' ? fallbackTitle : null);
        const hasTitle = title && title !== content;
        return (
          <li key={`${keyPrefix}-${index}`} style={{ marginBottom: 4 }}>
            <Typography component="span" variant="caption" sx={{ display: 'block', whiteSpace: 'pre-line' }}>
              {hasTitle && (
                <strong>{renderMarkdownBold(title)}</strong>
              )}
              {hasTitle && content ? ' — ' : null}
              {renderMarkdownBold(content || title)}
            </Typography>
          </li>
        );
      })}
    </ul>
  );
};

// Shared processing info: records successfully processed (total_data_points / data_points_processed), and selected_columns
const renderProcessingInfo = (results, showProcessingInfo) => {
  if (!showProcessingInfo) return null;
  const recordsProcessed = results.total_data_points ?? results.data_points_processed ?? results.total_data_points_processed;
  const cols = results.selected_columns;
  const colsStr = Array.isArray(cols) ? cols.join(', ') : (cols ? String(cols) : '');
  if (!results.processing_time && recordsProcessed == null && !colsStr) return null;
  return (
    <Typography variant="caption" className="widget-results-info" sx={{ display: 'block', mt: 1 }}>
      {results.processing_time != null && `${results.processing_time}s`}
      {recordsProcessed != null && ` | Records processed: ${recordsProcessed}`}
      {results.sampling_info && ` | ${results.sampling_info}`}
      {colsStr && ` | Columns: ${colsStr}`}
    </Typography>
  );
};

const WidgetResultsRenderer = ({ results, widgetType, showProcessingInfo = true }) => {
  if (!results) return null;

  if (results.error) {
    return (
      <Box className="widget-ai-friendly-message" role="alert">
        <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
          {"We couldn't show these results"}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
          {results.error}
        </Typography>
      </Box>
    );
  }

  const renderSummaryWidget = () => (
    <Box className="widget-results-container">
      {renderResultList(results.key_insights, 'insight', results.titles)}
      {renderProcessingInfo(results, showProcessingInfo)}
      {results.tags && results.tags.length > 0 && (
        <Box className="widget-results-tags" sx={{ mt: 2 }}>
          <Tags tags={results.tags} />
        </Box>
      )}
    </Box>
  );

  const renderAnalysisWidget = () => (
    <Box className="widget-results-container">
      {renderResultList(results.major_issues, 'issue', results.titles)}
      {renderProcessingInfo(results, showProcessingInfo)}
      {results.tags && results.tags.length > 0 && (
        <Box className="widget-results-tags" sx={{ mt: 2 }}>
          <Tags tags={results.tags} />
        </Box>
      )}
    </Box>
  );

  const renderSuccessWidget = () => (
    <Box className="widget-results-container">
      {renderResultList(results.major_positive_points, 'point', results.titles)}
      {renderProcessingInfo(results, showProcessingInfo)}
      {results.tags && results.tags.length > 0 && (
        <Box className="widget-results-tags" sx={{ mt: 2 }}>
          <Tags tags={results.tags} />
        </Box>
      )}
    </Box>
  );

  const renderRecommendationWidget = () => (
    <Box className="widget-results-container">
      {renderResultList(results.recommendations, 'rec', results.titles)}
      {renderProcessingInfo(results, showProcessingInfo)}
      {results.tags && results.tags.length > 0 && (
        <Box className="widget-results-tags" sx={{ mt: 2 }}>
          <Tags tags={results.tags} />
        </Box>
      )}
    </Box>
  );

  // Render based on widget type (enum: barchart, piechart, kpi, recommendation, major_issues, summary, success, custom_ai_card)
  switch (widgetType) {
    case 'summary':
    case 'custom_ai_card':
      return renderSummaryWidget();
    case 'analysis':
    case 'major_issues':
      return renderAnalysisWidget();
    case 'success':
      return renderSuccessWidget();
    case 'recommendation':
      return renderRecommendationWidget();
    default:
      return (
        <Box className="widget-results-container">
          <Typography variant="caption" className="widget-results-default">
            Results available for {widgetType} widget
          </Typography>
        </Box>
      );
  }
};

export default WidgetResultsRenderer;
